import { useParams, Link } from "react-router-dom";
import { useNoticia, useNoticiasRelacionadas } from "@/hooks/useNoticia";
import { useEffect } from "react";
import { toDriveDirectUrl } from "@/lib/utils";

export default function NoticiaDetalle() {
  const { id } = useParams<{ id: string }>();
  const noticiaId = Number(id);
  const { noticia, loading, error } = useNoticia(noticiaId);
  const { noticias: relacionadas } = useNoticiasRelacionadas(
    noticia?.categoria ?? null,
    noticiaId
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-white">
        <div className="flex items-center gap-3 text-masmed-gray-500">
          <i className="ri-loader-4-line animate-spin text-xl" />
          <span className="text-sm">Cargando noticia...</span>
        </div>
      </div>
    );
  }

  if (error || !noticia) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center bg-white px-4">
        <h1 className="text-2xl md:text-3xl text-masmed-gray-800 mb-4">Noticia no encontrada</h1>
        <Link
          to="/noticias"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-white text-sm rounded-md hover:bg-primary-dark transition-colors duration-200 cursor-pointer"
        >
          <i className="ri-arrow-left-line" />
          Volver a Noticias
        </Link>
      </div>
    );
  }

  // Generar contenido extendido basado en la noticia
  const contenido = generateContenido(noticia);

  return (
    <div>
      {/* Hero con imagen de la noticia */}
      <section className="relative w-full h-[380px] sm:h-[420px] md:h-[500px] lg:h-[560px] overflow-hidden">
        <img
          src={toDriveDirectUrl(noticia.imagen || "")}
          alt={noticia.titulo}
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/10" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-transparent" />

        {/* Breadcrumb */}
        <div className="relative z-10 px-4 md:px-8 lg:px-12 pt-24 md:pt-28">
          <div className="max-w-4xl mx-auto">
            <nav className="flex items-center gap-2 text-white/80 text-xs md:text-sm mb-4">
              <Link to="/" className="hover:text-white transition-colors">Inicio</Link>
              <i className="ri-arrow-right-s-line text-white/60" />
              <Link to="/noticias" className="hover:text-white transition-colors">Noticias</Link>
              <i className="ri-arrow-right-s-line text-white/60" />
              <span className="text-white/60 truncate max-w-[200px] md:max-w-sm">{noticia.titulo}</span>
            </nav>
          </div>
        </div>

        {/* Título y metadata */}
        <div className="relative z-10 px-4 md:px-8 lg:px-12 pb-10 md:pb-14">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-wrap items-center gap-2 md:gap-3 mb-3 md:mb-4">
              <span className="px-3 py-1 bg-primary text-white text-xs md:text-sm rounded-full"
              style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 600 }}>
                {noticia.categoria}
              </span>
              <span className="px-3 py-1 bg-white/20 text-white text-xs md:text-sm rounded-full backdrop-blur-sm"
              style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 600 }}>
                {noticia.segmento}
              </span>
              <span className="text-white/80 text-xs md:text-sm flex items-center gap-1">
                <i className="ri-map-pin-line" />
                {noticia.comuna}
              </span>
              <span className="text-white/80 text-xs md:text-sm flex items-center gap-1">
                <i className="ri-calendar-line" />
                {noticia.fecha}
              </span>
            </div>
            <h1 className="text-white text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold leading-tight drop-shadow-lg"
            style={{ fontFamily: "'Utendo', 'Nunito', 'Inter', sans-serif" }}>
              {noticia.titulo}
            </h1>
          </div>
        </div>
      </section>

      {/* Contenido principal */}
      <section className="py-10 md:py-14 lg:py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-8 lg:px-12">
          {/* Línea decorativa */}
          <div className="w-16 h-1 bg-primary rounded-full mb-6 md:mb-8" />

          {/* Contenido */}
          <div className="prose prose-sm md:prose-base max-w-none text-masmed-gray-700 leading-relaxed">
            {contenido.map((parrafo, i) => (
              <p key={i} className="mb-4 md:mb-5 text-sm md:text-base leading-7 text-masmed-gray-600">
                {parrafo}
              </p>
            ))}
          </div>

          {/* Compartir */}
          <div className="mt-10 md:mt-12 pt-6 border-t border-masmed-gray-200">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <span className="text-masmed-gray-500 text-sm mb-2 block"
                style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 600 }}>
                  Compartir:
                </span>
                <div className="flex items-center gap-2">
                  {[
                    { icon: "ri-facebook-fill", color: "bg-social-facebook" },
                    { icon: "ri-twitter-x-fill", color: "bg-masmed-gray-800" },
                    { icon: "ri-linkedin-fill", color: "bg-social-linkedin" },
                    { icon: "ri-whatsapp-fill", color: "bg-green-500" },
                  ].map((social) => (
                    <button
                      key={social.icon}
                      className={`w-9 h-9 rounded-full ${social.color} text-white flex items-center justify-center hover:scale-110 transition-transform duration-200 cursor-pointer`}
                      aria-label="Compartir"
                    >
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className={social.icon} />
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <Link
                to="/noticias"
                className="inline-flex items-center gap-2 px-5 py-2.5 border border-primary text-primary text-sm rounded-md hover:bg-primary hover:text-white transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line" />
                Volver a Noticias
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Noticias relacionadas */}
      {relacionadas.length > 0 && (
        <section className="py-10 md:py-14 bg-masmed-gray-50">
          <div className="max-w-6xl mx-auto px-4 md:px-8 lg:px-12">
            <div className="flex items-center gap-3 mb-6 md:mb-8">
              <div className="w-1 h-8 bg-primary rounded-full" />
              <h2 className="text-masmed-gray-800 text-xl md:text-2xl"
              style={{ fontFamily: "'Utendo', 'Nunito', 'Inter', sans-serif", fontWeight: 700 }}>
                Noticias relacionadas
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {relacionadas.map((rel) => (
                <Link
                  key={rel.id}
                  to={`/noticias/${rel.id}`}
                  className="bg-white rounded-2xl overflow-hidden flex flex-col border border-masmed-gray-200 shadow-sm transition-shadow duration-200 hover:shadow-card-hover cursor-pointer"
                >
                  <div className="h-40 md:h-48 w-full overflow-hidden bg-masmed-gray-100">
                    <img
                      src={toDriveDirectUrl(rel.imagen || "")}
                      alt={rel.titulo}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="p-4 md:p-5 flex-1 flex flex-col">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <span className="text-primary text-xs"
                      style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 600 }}>
                        {rel.comuna}
                      </span>
                      <span className="text-masmed-gray-400 text-xs">
                        {rel.fecha}
                      </span>
                    </div>
                    <h3 className="text-sm md:text-base text-masmed-gray-900 leading-snug mb-2 line-clamp-2"
                    style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 700 }}>
                      {rel.titulo}
                    </h3>
                    <p className="text-masmed-gray-500 text-xs md:text-sm leading-relaxed line-clamp-2 mb-3 flex-1">
                      {rel.extracto}
                    </p>
                    <div className="mt-auto flex items-center text-primary text-sm"
                    style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 600 }}>
                      Leer más
                      <span className="w-4 h-4 flex items-center justify-center ml-1">
                        <i className="ri-arrow-right-line" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function generateContenido(noticia: {
  extracto: string | null;
  comuna: string | null;
  categoria: string | null;
  segmento: string | null;
  titulo: string;
}): string[] {
  const base = noticia.extracto || noticia.titulo;
  const comuna = noticia.comuna || "la región";
  const categoria = noticia.categoria || "Actualidad";
  const segmento = noticia.segmento || "Actividades";

  return [
    `${base} En el marco de las iniciativas impulsadas por MásMed Tarapacá, esta actividad representa un paso significativo hacia el fortalecimiento del sistema de salud en la región.`,
    `La actividad se desarrolló en ${comuna}, zona que presenta importantes desafíos en materia de cobertura médica y acceso a especialistas. Durante la jornada, se destacó la necesidad de contar con información precisa sobre las brechas de capital humano médico para la toma de decisiones en salud pública.`,
    `"Es fundamental comprender las realidades territoriales de cada comuna para poder diseñar políticas de salud efectivas y pertinentes", señalaron los representantes del proyecto. El trabajo de campo realizado en ${comuna} permitió recoger datos valiosos sobre las necesidades de la comunidad local.`,
    `La iniciativa, enmarcada dentro de la categoría ${categoria}, busca generar conocimiento aplicable que contribuya a reducir las brechas de especialistas médicos en la Región de Tarapacá. A través de esta actividad de ${segmento.toLowerCase()}, MásMed continúa avanzando en su objetivo de construir una salud más cercana, equitativa y conectada con las necesidades reales de los territorios.`,
    `El proyecto MásMed Tarapacá, ejecutado por la Universidad Arturo Prat y financiado por el Gobierno Regional de Tarapacá a través del Fondo Regional para la Productividad y el Desarrollo (FRPD), seguirá desarrollando actividades de levantamiento de información y vinculación territorial durante los próximos meses.`,
  ];
}